import { reactive } from 'vue'
import { apiFetch, setAuthToken } from './api'

export type AccountRole = 'admin-master' | 'admin-obra' | 'user'

export interface Account {
  uid: string
  username: string
  password?: string
  role: AccountRole
  systemId?: string
  systemUsername?: string
  roleId: string
  subordinateUsernames: string[]
  hasSubordinates: boolean
  name: string
  avatar?: string
  email?: string
  // YYYY-MM-DD; enviado só ao criar (obrigatório) ou ao alterar
  birthDate?: string
  isAnalyst?: boolean
  lastOnlineAt?: string
}

interface LoginResponse {
  account: Account
  token: string
  suspended?: boolean
  newVersion?: boolean
  currentVersion?: string
}

interface UpsertResponse {
  ok: boolean
  account: Account
  token?: string
}

const state = reactive({
  items: [] as Account[],
  loading: false
})

function normalizeRole(value: string): AccountRole {
  if (value === 'admin-master') return 'admin-master'
  if (value === 'admin-obra') return 'admin-obra'
  if (value === 'admin') return 'admin-master'
  return 'user'
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function mapAccount(item: unknown): Account {
  const record = asRecord(item)
  const role = normalizeRole(String(record.role || 'user'))
  const username = String(record.username || '')
  const subordinateUsernames = Array.isArray(record.subordinateUsernames)
    ? record.subordinateUsernames.filter((value): value is string => typeof value === 'string')
    : []
  return {
    uid: String(record.uid || username),
    username,
    password: String(record.password || ''),
    role,
    systemId: String(record.systemId || ''),
    systemUsername: String(record.systemUsername || ''),
    roleId: String(record.roleId || (role !== 'user' ? 'role-admin' : 'role-user')),
    subordinateUsernames,
    hasSubordinates: Boolean(record.hasSubordinates) || subordinateUsernames.length > 0,
    name: String(record.name || username || 'Sem nome'),
    avatar: String(record.avatar || ''),
    email: String(record.email || ''),
    isAnalyst: Boolean(record.isAnalyst),
    lastOnlineAt: String(record.lastOnlineAt || '')
  }
}

export const accountsStore = {
  state,
  all() {
    return state.items
  },
  async refresh() {
    state.loading = true
    try {
      const rows = await apiFetch<unknown[]>('/accounts')
      state.items = rows.map(mapAccount)
      return state.items
    } finally {
      state.loading = false
    }
  },
  async login(username: string, password: string, systemUsername = '', keepLoggedIn = false) {
    const payload = await apiFetch<LoginResponse>(
      '/login',
      {
        method: 'POST',
        body: JSON.stringify({ username, password, systemUsername })
      },
      false
    )
    setAuthToken(payload.token, keepLoggedIn)
    const account = mapAccount(payload.account)
    return { ...account, suspended: payload.suspended || false, newVersion: payload.newVersion || false }
  },
  async registerProject(username: string, password: string, name: string, projectName: string, systemUsername: string, plan = 'gratuito', acceptedTerms = false, birthDate = '') {
    const payload = await apiFetch<LoginResponse & { ok: boolean }>(
      '/register-system',
      {
        method: 'POST',
        body: JSON.stringify({ username, password, name, projectName, systemUsername, plan, acceptedTerms, birthDate })
      },
      false
    )
    setAuthToken(payload.token)
    return mapAccount(payload.account)
  },
  async upsert(account: Account) {
    const payload = await apiFetch<UpsertResponse>('/accounts', {
      method: 'POST',
      body: JSON.stringify({
        uid: account.uid,
        username: account.username,
        password: account.password,
        role: account.role,
        roleId: account.roleId,
        subordinateUsernames: account.subordinateUsernames || [],
        name: account.name,
        avatar: account.avatar || '',
        email: account.email || '',
        birthDate: account.birthDate || ''
      })
    })
    if (payload.token) {
      setAuthToken(payload.token)
    }
    await this.refresh()
    return mapAccount(payload.account)
  },
  async remove(uid: string) {
    await apiFetch<{ ok: boolean }>(`/accounts/${encodeURIComponent(uid)}`, {
      method: 'DELETE'
    })
    await this.refresh()
  }
}
